import axios, { AxiosResponse } from 'axios';
import { store } from '../stores/store';
import { User, UserFormValues } from '../models/User';
import { ShopifyAuth } from '../models/ShopifyAuth';
import { Setting } from '../models/Setting';
import { SyncLog } from '../models/SyncLog';
import { Group, GroupFormValues } from '../models/Group';
import { PricePullHistory, Pricing } from '../models/Pricing';
import { LocationInventorySync } from '../models/LocationInventorySync';
import {
    FulfillmentAuditDetail,
    FulfillmentAuditPageResult,
    FulfillmentAuditQuery,
    FulfillmentAuditSummary,
    FulfillmentAuditTraverseCheck,
} from '../models/FulfillmentAudit';
import {
    PaymentReconciliationActionResult,
    PaymentReconciliationArchiveActionResult,
    PaymentReconciliationDetail,
    PaymentReconciliationManualReview,
    PaymentReconciliationPageResult,
    PaymentReconciliationQuery,
    PaymentReconciliationSummary,
} from '../models/PaymentReconciliation';

type ShopifyAppBridgeGlobal = {
    idToken: () => Promise<string>;
};

const getShopifyIdToken = async () => {
    const shopify = (window as typeof window & { shopify?: ShopifyAppBridgeGlobal }).shopify;
    if (!shopify?.idToken) {
        throw new Error('Shopify staff authentication is unavailable. Reopen the app from Shopify admin and try again.');
    }

    return shopify.idToken();
};

const getShopifyStaffHeaders = async () => ({
    'X-Shopify-ID-Token': await getShopifyIdToken(),
});

//axios.defaults.baseURL = 'http://localhost:5000/api';
//axios.defaults.baseURL = 'https://rh.ngrok.app/api';
axios.defaults.baseURL = process.env.REACT_APP_API_URL;

// this sends bearer token (if available) with each request
axios.interceptors.request.use(config => {
    const token = store.commonStore.token;
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config;
})

// Used to create fake delay, for testing loading states
const sleep = (delay: number) => {
    return new Promise((resolve) => {
        setTimeout(resolve, delay);
    })
}

// Used to create fake delay, for testing loading states
axios.interceptors.response.use(async response => {
    try {
        //await sleep(500);
        return response;
    } catch (error) {
        console.log(error);
        return await Promise.reject(error);
    }
})

const responseBody = <T> (response: AxiosResponse<T>) => response.data;

const requests = {
    get: <T> (url: string) => axios.get<T>(url).then(responseBody),
    post: <T> (url: string, body: {}) => axios.post<T>(url, body).then(responseBody),
    put: <T> (url: string, body: {}) => axios.put<T>(url, body).then(responseBody),
    del: <T> (url: string) => axios.delete<T>(url).then(responseBody),
}

const Account = {
    auth: (user: UserFormValues) => requests.post<string>('/account/auth', user),    
    embeddedauth: (user: ShopifyAuth) => requests.post<string>('/account/embeddedauth', user),
    embeddedlogin: async (user: ShopifyAuth) => requests.post<User>('/account/embeddedlogin', {
        ...user,
        // Query-string ID tokens are short-lived. Ask App Bridge for a fresh one
        // immediately before the backend exchanges it for offline API access.
        id_token: await getShopifyIdToken(),
    }),
    login: (user: ShopifyAuth) => requests.post<User>('/account/login', user),
    current: () => requests.get<User>('/account')    
}

const Settings = {
    details: () => requests.get<Setting>('/settings'),
    update: (setting: Setting) => axios.put<void>(`/settings/`, setting),
}

const SyncLogInfo = {
    details: () => requests.get<SyncLog>('/synclog/details'),
    list: () => requests.get<SyncLog[]>('/synclog'),
    sendTestAlert: () => requests.post<{ message: string }>('/synclog/test-alert', {}),
}

const FulfillmentAudit = {
    list: (query: FulfillmentAuditQuery) =>
        axios.get<FulfillmentAuditPageResult>('/fulfillmentaudit', { params: query }).then(responseBody),
    summary: (query: Pick<FulfillmentAuditQuery, 'search' | 'status' | 'days'>) =>
        axios.get<FulfillmentAuditSummary>('/fulfillmentaudit/summary', { params: query }).then(responseBody),
    details: (id: number) => requests.get<FulfillmentAuditDetail>(`/fulfillmentaudit/${id}`),
    checkTraverseSource: (id: number) =>
        requests.get<FulfillmentAuditTraverseCheck>(`/fulfillmentaudit/${id}/traverse-source`),
}

const PaymentReconciliation = {
    list: (query: PaymentReconciliationQuery) =>
        axios.get<PaymentReconciliationPageResult>('/paymentreconciliation', { params: query }).then(responseBody),
    exportCsv: (query: Pick<PaymentReconciliationQuery, 'search' | 'view' | 'sort'>) =>
        axios.get<Blob>('/paymentreconciliation/export', {
            params: query,
            responseType: 'blob',
        }),
    summary: () => requests.get<PaymentReconciliationSummary>('/paymentreconciliation/summary'),
    details: (id: number) => requests.get<PaymentReconciliationDetail>(`/paymentreconciliation/${id}`),
    manualReview: async (shopifyOrderId: number) =>
        axios.get<PaymentReconciliationManualReview>(
            `/paymentreconciliation/orders/${shopifyOrderId}/manual-review`,
            { headers: await getShopifyStaffHeaders() }
        ).then(responseBody),
    markPaidManually: async (shopifyOrderId: number, staffNote: string) =>
        axios.post<PaymentReconciliationActionResult>(
            `/paymentreconciliation/orders/${shopifyOrderId}/mark-paid`,
            { staffNote },
            { headers: await getShopifyStaffHeaders() }
        ).then(responseBody),
    archive: async (shopifyOrderId: number, note: string) =>
        axios.post<PaymentReconciliationArchiveActionResult>(
            `/paymentreconciliation/orders/${shopifyOrderId}/archive`,
            { note: note.trim() || null },
            { headers: await getShopifyStaffHeaders() }
        ).then(responseBody),
    restore: async (shopifyOrderId: number) =>
        axios.post<PaymentReconciliationArchiveActionResult>(
            `/paymentreconciliation/orders/${shopifyOrderId}/restore`,
            {},
            { headers: await getShopifyStaffHeaders() }
        ).then(responseBody),
}


const PricingInfo = {
    details: () => requests.get<Pricing>('/pricing'),
    restore: (pricing: PricePullHistory) => axios.post<PricePullHistory>(`/pricing/`, pricing),
}


const LocationInventorySyncSettings = {
    list: () => requests.get<LocationInventorySync[]>('/locationinventorysync'),
    update: (locations: LocationInventorySync[]) => axios.put<void>('/locationinventorysync', locations),
}

const Groups = {
    list: () => requests.get<Group[]>('/groups'),
    details: (id: string) => requests.get<Group>(`/groups/${id}`),
    create: (group: GroupFormValues) => axios.post<void>('/groups', group),
    update: (group: GroupFormValues) => axios.put<void>(`/groups/${group.id}`, group),
    delete: (id: string) => axios.delete<void>(`/groups/${id}`)
}

const agent = {
    Account,
    Settings,
    Groups,
    SyncLogInfo,
    PricingInfo,
    LocationInventorySyncSettings,
    FulfillmentAudit,
    PaymentReconciliation,
}
 
export default agent;
